import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MainLayout } from '@templates/index';
import { Badge, Button, Card, Input, Toast } from '@atoms/index';
import { CancelOrderModal } from '@organisms/CancelOrderModal';
import { orderService } from '@services/orderService';
import { formatCurrency } from '@utils/currency';
import { formatOrderStatus, formatPaymentMethod } from '@utils/formatters';
import type { Order, OrderStatus } from '@/types';

const getStatusInfo = (status: OrderStatus) => {
  const formattedLabel = formatOrderStatus(status);
  const statusMap: Partial<
    Record<OrderStatus, { variant: 'default' | 'success' | 'warning' | 'danger' | 'info' }>
  > = {
    CREATED: { variant: 'default' },
    SCHEDULED: { variant: 'info' },
    PENDING: { variant: 'warning' },
    PREPARING: { variant: 'warning' },
    READY: { variant: 'info' },
    ON_WAY: { variant: 'info' },
    DELIVERED: { variant: 'success' },
    CANCELLED: { variant: 'danger' },
  };
  return { label: formattedLabel, variant: statusMap[status]?.variant || 'default' };
};

const getStatusSteps = (currentStatus: OrderStatus) => {
  const steps = [
    { status: 'CREATED' as OrderStatus, label: 'Sipariş Alındı' },
    { status: 'PREPARING' as OrderStatus, label: 'Hazırlanıyor' },
    { status: 'READY' as OrderStatus, label: 'Hazır' },
    { status: 'ON_WAY' as OrderStatus, label: 'Yolda' },
    { status: 'DELIVERED' as OrderStatus, label: 'Teslim Edildi' },
  ];

  const statusToStepMap: Record<string, number> = {
    CREATED: 0,
    SCHEDULED: 0,
    PENDING: 0,
    PREPARING: 1,
    READY: 2,
    ON_WAY: 3,
    DELIVERED: 4,
  };
  const stepIndex = statusToStepMap[currentStatus] ?? 0;

  return steps.map((step, index) => ({
    ...step,
    isCompleted: index <= stepIndex,
    isCurrent: index === stepIndex,
  }));
};

const GuestOrderTrackingPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(Boolean(token));
  const [error, setError] = useState<string | null>(null);
  const [resendOrderNumber, setResendOrderNumber] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCancelSubmitting, setIsCancelSubmitting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const loadOrder = useCallback(async (trackingToken: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await orderService.trackGuestOrder(trackingToken);
      setOrder(data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Sipariş yüklenemedi';
      setError(message);
      setOrder(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!token) {
      setIsLoading(false);
      return;
    }

    loadOrder(token);

    const interval = setInterval(() => {
      loadOrder(token);
    }, 10000);

    return () => clearInterval(interval);
  }, [token, loadOrder]);

  const canCancelOrder = useMemo(
    () => order && ['CREATED', 'PENDING', 'SCHEDULED'].includes(order.status || ''),
    [order],
  );

  const handleResendLink = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsResending(true);
    setResendMessage(null);

    try {
      const message = await orderService.resendGuestTrackingLink(
        resendOrderNumber.trim(),
        resendEmail.trim(),
      );
      setResendMessage(message);
    } catch (err: unknown) {
      setResendMessage(
        err instanceof Error ? err.message : 'Bağlantı gönderilemedi. Lütfen tekrar deneyin.',
      );
    } finally {
      setIsResending(false);
    }
  };

  const handleConfirmCancel = async (reason?: string) => {
    if (!token || !order) return;

    setIsCancelSubmitting(true);
    try {
      const updated = await orderService.cancelGuestOrder(token, reason);
      setOrder(updated);
      setShowCancelModal(false);
      setToast({ message: 'Siparişiniz iptal edildi.', type: 'success' });
    } catch (err: unknown) {
      setToast({
        message: err instanceof Error ? err.message : 'Sipariş iptal edilemedi.',
        type: 'error',
      });
    } finally {
      setIsCancelSubmitting(false);
    }
  };

  if (!token) {
    return (
      <MainLayout>
        <div className="max-w-lg mx-auto py-12">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Sipariş Takibi</h1>
            <p className="text-gray-600">
              Takip bağlantınız kaybolduysa sipariş numaranız ve e-posta adresinizle yeni bağlantı isteyebilirsiniz.
            </p>
          </div>

          <Card className="space-y-4">
            <form onSubmit={handleResendLink} className="space-y-4">
              <Input
                label="Sipariş Numarası"
                placeholder="LY-2026-000123"
                value={resendOrderNumber}
                onChange={(e) => setResendOrderNumber(e.target.value)}
                required
              />
              <Input
                label="E-posta"
                type="email"
                placeholder="ornek@email.com"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                required
              />
              <Button type="submit" fullWidth disabled={isResending}>
                {isResending ? 'Gönderiliyor...' : 'Takip Bağlantısı Gönder'}
              </Button>
            </form>

            {resendMessage && (
              <p className="text-sm text-gray-600 bg-gray-50 rounded-lg px-4 py-3">{resendMessage}</p>
            )}
          </Card>

          <div className="text-center mt-6">
            <Link to="/" className="text-primary font-medium hover:underline">
              Ana sayfaya dön
            </Link>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (isLoading && !order) {
    return (
      <MainLayout>
        <div className="flex min-h-[50vh] items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-gray-600">Siparişiniz yükleniyor...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error || !order) {
    return (
      <MainLayout>
        <div className="max-w-lg mx-auto py-16 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-3">Takip Bağlantısı Geçersiz</h1>
          <p className="text-gray-600 mb-8">
            {error || 'Bağlantının süresi dolmuş veya geçersiz olabilir.'}
          </p>
          <Button onClick={() => window.location.assign('/siparis-takip')}>
            Yeni Bağlantı İste
          </Button>
        </div>
      </MainLayout>
    );
  }

  const orderStatus = (order.status || 'CREATED') as OrderStatus;
  const statusInfo = getStatusInfo(orderStatus);
  const steps = getStatusSteps(orderStatus);
  const restaurant = (order as Order & { restaurant?: { name?: string } }).restaurant;

  return (
    <MainLayout>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible
          onClose={() => setToast(null)}
          duration={5000}
        />
      )}

      {order && (
        <CancelOrderModal
          isOpen={showCancelModal}
          order={{ ...order, restaurant: restaurant ?? undefined }}
          isSubmitting={isCancelSubmitting}
          onConfirm={handleConfirmCancel}
          onClose={() => !isCancelSubmitting && setShowCancelModal(false)}
        />
      )}

      <div className="max-w-3xl mx-auto py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Sipariş Takibi</h1>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-gray-500">Sipariş No: {order.orderNumber}</span>
            <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
            {canCancelOrder && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowCancelModal(true)}
                className="border-red-200 text-red-700 hover:bg-red-50"
              >
                Siparişi İptal Et
              </Button>
            )}
          </div>
        </div>

        {orderStatus !== 'CANCELLED' && (
          <Card className="mb-6">
            <div className="flex justify-between gap-2">
              {steps.map((step) => (
                <div key={step.status} className="flex-1 text-center">
                  <div
                    className={`mx-auto mb-2 h-3 w-3 rounded-full ${
                      step.isCompleted ? 'bg-primary' : 'bg-gray-200'
                    } ${step.isCurrent ? 'ring-4 ring-primary/20' : ''}`}
                  />
                  <p
                    className={`text-xs ${
                      step.isCurrent ? 'font-semibold text-primary' : 'text-gray-500'
                    }`}
                  >
                    {step.label}
                  </p>
                </div>
              ))}
            </div>
          </Card>
        )}

        <div className="grid gap-6 md:grid-cols-2">
          <Card className="space-y-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500">Teslimat Adresi</p>
              <p className="font-medium text-gray-900">{order.deliveryAddress.fullAddress}</p>
            </div>
            {restaurant?.name && (
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">Restoran</p>
                <p className="font-medium text-gray-900">{restaurant.name}</p>
              </div>
            )}
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500">Ödeme</p>
              <p className="font-medium text-gray-900">
                {formatPaymentMethod(order.paymentMethod || 'CASH')}
              </p>
            </div>
            <div className="flex justify-between border-t border-gray-100 pt-3">
              <span className="text-gray-600">Toplam</span>
              <span className="font-bold text-gray-900">{formatCurrency(order.total)}</span>
            </div>
          </Card>

          <Card>
            <p className="text-xs uppercase tracking-wide text-gray-500 mb-3">Sipariş Özeti</p>
            <div className="space-y-3">
              {(order.items || []).map((item, index) => (
                <div key={`${item.menuItemId}-${index}`} className="flex justify-between gap-3 text-sm">
                  <div>
                    <p className="font-medium text-gray-900">
                      {item.quantity}x {item.menuItemName}
                    </p>
                    {item.optionNames && item.optionNames.length > 0 && (
                      <p className="text-xs text-gray-500">{item.optionNames.join(', ')}</p>
                    )}
                  </div>
                  <span className="font-medium text-gray-900">
                    {formatCurrency(item.unitPrice * item.quantity)}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="mt-8 text-center">
          <Link to="/siparis-takip" className="text-sm text-gray-500 hover:text-primary">
            Bağlantı kayboldu mu? Yeni takip bağlantısı iste
          </Link>
        </div>
      </div>
    </MainLayout>
  );
};

export default GuestOrderTrackingPage;
